import { EditorConfig, LexicalEditor, LexicalNode } from "lexical";
import { Decorable, GConstructor } from "./general";

export interface ContainerPayload {
  width?: number;
  height?: number;
};

export interface ContainerMixinPayload {
  __width?: number;
  __height?: number;
}

export type SerializedContainer = {
  width?: number;
  height?: number;
};

type ContainerCtor = GConstructor<Decorable & ContainerMixinPayload>;

export function ContainerMixin<TBase extends ContainerCtor>(Base: TBase) {
  return class Container extends Base {
    __width!: number;
    __height!: number;

    constructor(
      ...args: any[]
    ) {
      const [width, height, ...rest] = args;
      super(...rest);
      this.__width = width || 0;
      this.__height = height || 0;
    }

    createDOM(config: EditorConfig, editor: LexicalEditor) {
      const dom = super.createDOM(config, editor);
      this.applyDimensionsStyles(dom);
      return dom;
    }

    updateDOM(_prevNode: unknown, _dom: HTMLElement, _config: EditorConfig): boolean {
      super.updateDOM(_prevNode, _dom, _config);
      this.applyDimensionsStyles(_dom);
      return false;
    }

    getWidth() {
      const self = this.getLatest();
      return self.__width;
    }

    getHeight() {
      const self = this.getLatest();
      return self.__height;
    }

    setDimensions(width: number, height: number) {
      const self = this.getWritable();
      self.__width = width;
      self.__height = height;
    }

    applyDimensionsStyles(dom: HTMLElement) {
      const self = this.getLatest();
      dom.style.width = `${self.__width}px`;
      dom.style.height = `${self.__height}px`;
    }
  };
}

type Container = InstanceType<ReturnType<typeof ContainerMixin<ContainerCtor>>>;

export const $isContainerNode = (node: unknown): node is Container => {
  return Boolean(node && typeof node === 'object' && '__width' in node && '__height' in node);
}